import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import {
  getAdminProfile,
  getStaffProfile,
  updateAdminProfile,
  updateStaffProfile,
} from "../api/authApi";
import { loginSuccess } from "../redux/authSlice";
import "./css/ProfilePage.css";
import { toast } from "react-toastify";

// ─── Profile (logged-in admin) ─────────────────────────────────────────────
// GET /api/auth/admin/profile             → fetch latest profile on mount
// PUT /api/auth/admin/profile   { name, phone, department }
// Backend: authController.js → getAdminProfile / updateAdminProfile (req.user.id from JWT)
// Email is intentionally read-only here since it's tied to login credentials.

// Must match the Admin model's `department` enum exactly
const DEPARTMENT_OPTIONS = [
  "Operations",
  "Support",
  "Finance",
  "Marketing",
  "HR",
  "IT",
  "Management",
  "Head Office",
  "Sales",
  "office",
];

const ProfilePage = () => {
  const dispatch = useDispatch();
  const token = useSelector((state) => state.auth.token);
  const reduxAdmin = useSelector((state) => state.auth.admin);

  // ✅ Role comes from the logged-in user object in redux (localStorage("admin")),
  // NOT a separate "role" key — that key was never being set, so this was
  // always falling through to the staff branch.
  const role = reduxAdmin?.role;
  const isSuperAdmin = role === "SUPERADMIN";
  const isAdmin = role === "ADMIN";
  console.log(isSuperAdmin, "<<< super admin check");

  // Local copy of the profile — starts from redux, then gets refreshed from the API
  const [admin, setAdmin] = useState(reduxAdmin || null);
  const [pageLoading, setPageLoading] = useState(true);
  const [pageError, setPageError] = useState("");

  const [editMode, setEditMode] = useState(false);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [department, setDepartment] = useState("");

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [saving, setSaving] = useState(false);

  // ── Fetch latest profile from backend on mount ──────────────────────────
  useEffect(() => {
    const fetchProfile = async () => {
      setPageLoading(true);
      setPageError("");
      try {
        const data = isSuperAdmin
          ? await getAdminProfile()
          : await getStaffProfile();
        if (data.success) {
          // ✅ Admin API returns { admin }, Staff API returns { staff } —
          // normalize into one shape so the rest of the component doesn't care.
          const profile = data.admin || data.staff;
          setAdmin(profile);
          // Keep redux/localStorage in sync with the freshest profile too
          // (only if we actually got a profile back — never dispatch undefined)
          if (profile) {
            dispatch(loginSuccess({ token, admin: profile }));
          }
        } else {
          setPageError(data.message || "Failed to load profile.");
        }
      } catch (err) {
        setPageError(
          err?.response?.data?.message ||
            err?.message ||
            "Server error while loading profile.",
        );
      } finally {
        setPageLoading(false);
      }
    };
    fetchProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const initials = (admin?.name || "A")
    .split(" ")
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  const enterEditMode = () => {
    setName(admin?.name || "");
    setPhone(admin?.phone || "");
    setDepartment(admin?.department || "Sales");
    setError("");
    setSuccess("");
    setEditMode(true);
  };

  const handleCancel = () => {
    setError("");
    setSuccess("");
    setEditMode(false);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!name.trim()) {
      setError("Name cannot be empty.");
      return;
    }

    setSaving(true);
    try {
      const data = isSuperAdmin
        ? await updateAdminProfile({ name, phone, department })
        : await updateStaffProfile({ name, phone, department });

      if (data.success) {
        // ✅ Same normalization here — Admin API returns { admin }, Staff API returns { staff }
        const profile = data.admin || data.staff;
        console.log("Profile updated successfully:", data);
        setSuccess("Profile updated successfully.");
        setAdmin(profile);
        // Refresh redux state + localStorage("admin") with latest values
        // (guarded — never dispatch an undefined profile, that's what caused
        // localStorage("admin") to become the string "undefined" earlier)
        if (profile) {
          dispatch(loginSuccess({ token, admin: profile }));
        }
        setEditMode(false);
        toast.success("Profile updated successfully!");
      } else {
        setError(data.message || "Failed to update profile.");
        toast.error(data.message || "Failed to update profile.");
      }
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Server error. Please try later.",
      );

      toast.error(
        err?.response?.data?.message ||
          err?.message ||
          "Server error. Please try later.",
      );
    } finally {
      setSaving(false);
    }
  };

  if (pageLoading) {
    return (
      <div className="pf-wrapper">
        <div className="pf-card pf-loading">Loading profile...</div>
      </div>
    );
  }

  return (
    <div className="pf-wrapper">
      <div className="pf-card">
        {/* ── Header ── */}
        <div className="pf-header">
          <div className="pf-avatar">{initials}</div>
          <div>
            <h2 className="pf-name">{admin?.name || "Admin"}</h2>
            <span className="pf-role-badge">{admin?.role || "ADMIN"}</span>
          </div>
        </div>

        {pageError && <p className="pf-error-text">{pageError}</p>}

        {/* ── View Mode ── */}
        {!editMode ? (
          <div className="pf-details">
            <div className="pf-row">
              <span className="pf-label">Email</span>
              <span className="pf-value">{admin?.email || "-"}</span>
            </div>
            <div className="pf-row">
              <span className="pf-label">Phone</span>
              <span className="pf-value">{admin?.phone || "-"}</span>
            </div>
            <div className="pf-row">
              <span className="pf-label">Department</span>
              <span className="pf-value">{admin?.department || "-"}</span>
            </div>
            <div className="pf-row">
              <span className="pf-label">Role</span>
              <span className="pf-value">{admin?.role || "-"}</span>
            </div>

            <div className="pf-actions">
              <button className="pf-btn pf-btn-primary" onClick={enterEditMode}>
                Edit Profile
              </button>
              <Link to="/change-password" className="pf-btn pf-btn-secondary">
                Change Password
              </Link>
            </div>
          </div>
        ) : (
          /* ── Edit Mode ── */
          <form onSubmit={handleSave} className="pf-details">
            <label className="pf-label-input">Name</label>
            <input
              disabled={!isSuperAdmin && !isAdmin}
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="pf-input"
            />

            <label className="pf-label-input">Email</label>
            <input
              type="email"
              value={admin?.email || ""}
              disabled
              className="pf-input pf-input-disabled"
            />
            <p className="pf-hint">
              Email is linked to your login and can't be changed here.
            </p>

            <label className="pf-label-input">Phone</label>
            <input
              disabled={!isSuperAdmin && !isAdmin}
              type="text"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className="pf-input"
              placeholder="Enter phone number"
            />

            <label className="pf-label-input">Department</label>
            <select
              disabled={!isSuperAdmin && !isAdmin}
              value={department}
              onChange={(e) => setDepartment(e.target.value)}
              className="pf-input pf-select"
            >
              {DEPARTMENT_OPTIONS.map((dept) => (
                <option key={dept} value={dept}>
                  {dept}
                </option>
              ))}
            </select>

            {error && <p className="pf-error-text">{error}</p>}
            {success && <p className="pf-success-text">{success}</p>}

            <div className="pf-actions">
              <button
                type="submit"
                disabled={saving}
                className="pf-btn pf-btn-primary"
              >
                {saving ? "Saving..." : "Save Changes"}
              </button>
              <button
                type="button"
                className="pf-btn pf-btn-secondary"
                onClick={handleCancel}
              >
                Cancel
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default ProfilePage;
